// src/pages/LandingPage.tsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LoginPage } from './LoginPage';
import { SignupPage } from './SignupPage';

export function LandingPage() {
  const [showAuth, setShowAuth] = useState<'login' | 'signup' | null>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Hero Section */}
      <div className="min-h-screen bg-gradient-to-br from-brand-900 via-primary-800 to-accent-800 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='10' cy='10' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        {/* Navigation */}
        <nav className="relative z-10 flex justify-between items-center p-6 lg:px-12">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-primary-600 font-bold text-xl">C</span>
            </div>
            <span className="text-white text-2xl font-bold tracking-tight">Chatutu</span>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('features')} className="text-white hover:text-primary-200 transition-all duration-200 font-medium">
              Features
            </button>
            <Link to="/blog" className="text-white hover:text-primary-200 transition-all duration-200 font-medium">
              Blog
            </Link>
            <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-primary-200 transition-all duration-200 font-medium">
              Testimonials
            </button>
            <button onClick={() => scrollToSection('reports')} className="text-white hover:text-primary-200 transition-all duration-200 font-medium">
              Reports
            </button>
            <button
              onClick={() => setShowAuth('login')}
              className="text-white hover:text-primary-200 transition-all duration-200 font-medium"
            >
              Login
            </button>
            <button
              onClick={() => setShowAuth('signup')}
              className="bg-white text-primary-600 px-6 py-2.5 rounded-full font-semibold hover:bg-primary-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Sign Up
            </button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[85vh] text-center px-6 lg:px-12">
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight max-w-6xl">
            Automate Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-300 to-primary-300 mt-2">
              Instagram Conversations
            </span>
          </h1>
          <p className="text-xl lg:text-2xl text-brand-100 mb-10 max-w-4xl leading-relaxed font-light">
            Transform your Instagram engagement with AI-powered responses and intelligent automation. Connect your Instagram business account, create smart response templates, and let our advanced AI chatbot handle customer conversations 24/7 while you focus on growing your business and increasing sales.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <button
              onClick={() => setShowAuth('signup')}
              className="bg-white text-primary-600 px-10 py-4 rounded-full font-semibold text-lg hover:bg-primary-50 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:scale-105"
            >
              Start Free Trial
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="border-2 border-white text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-primary-600 transition-all duration-200"
            >
              Learn More
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-brand-100 max-w-4xl">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-4 h-4 bg-success-400 rounded-full flex-shrink-0"></div>
              <span className="font-medium">Easy Setup</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-4 h-4 bg-accent-400 rounded-full flex-shrink-0"></div>
              <span className="font-medium">AI Powered</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-4 h-4 bg-primary-400 rounded-full flex-shrink-0"></div>
              <span className="font-medium">24/7 Automation</span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <button onClick={() => scrollToSection('features')} className="text-white opacity-75 hover:opacity-100 transition-opacity">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-24 bg-brand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-900 mb-6">
              Powerful Features for Instagram Success
            </h2>
            <p className="text-xl text-brand-600 max-w-3xl mx-auto leading-relaxed">
              Everything you need to automate your Instagram conversations and boost engagement with cutting-edge AI technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: '🤖',
                title: 'AI-Powered Responses',
                description: 'Intelligent AI generates personalized responses based on conversation context and your brand voice.',
                color: 'from-accent-500 to-accent-600'
              },
              {
                icon: '📝',
                title: 'Smart Templates',
                description: 'Create and manage reusable response templates for common inquiries and scenarios.',
                color: 'from-primary-500 to-primary-600'
              },
              {
                icon: '📊',
                title: 'Analytics Dashboard',
                description: 'Track conversation metrics, response rates, and engagement statistics in real-time.',
                color: 'from-success-500 to-success-600'
              },
              {
                icon: '🔗',
                title: 'Easy Instagram Connect',
                description: 'One-click connection to your Instagram Business account with secure OAuth integration.',
                color: 'from-warning-500 to-warning-600'
              },
              {
                icon: '⚡',
                title: 'Instant Responses',
                description: 'Respond to messages 24/7 with lightning-fast automated replies that feel human.',
                color: 'from-accent-600 to-accent-700'
              },
              {
                icon: '🎯',
                title: 'Custom Workflows',
                description: 'Set up automated sequences for lead nurturing, customer support, and sales follow-ups.',
                color: 'from-primary-600 to-primary-700'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-brand-100 hover:border-brand-200 transform hover:-translate-y-1">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-lg`}>
                  <span className="text-3xl">{feature.icon}</span>
                </div>
                <h3 className="text-2xl font-bold text-brand-900 mb-4">{feature.title}</h3>
                <p className="text-brand-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reports Section */}
      <section id="reports" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-900 mb-6">
              Proven Results
            </h2>
            <p className="text-xl text-brand-600 max-w-3xl mx-auto leading-relaxed">
              Real metrics from businesses using Chatutu to automate their Instagram conversations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {[
              { number: '500K+', label: 'Messages Automated', icon: '💬' },
              { number: '95%', label: 'Response Rate', icon: '📈' },
              { number: '24/7', label: 'Availability', icon: '⏰' },
              { number: '50+', label: 'Happy Customers', icon: '😊' }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-5xl lg:text-6xl font-bold text-primary-600 mb-4 group-hover:scale-110 transition-transform duration-300">{stat.number}</div>
                <div className="text-brand-600 font-semibold text-lg mb-2">{stat.label}</div>
                <div className="text-4xl">{stat.icon}</div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-brand-50 to-primary-50 rounded-3xl shadow-xl p-8 lg:p-12 border border-brand-100">
            <h3 className="text-3xl font-bold text-brand-900 mb-12 text-center">
              Performance Dashboard Preview
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-100 hover:shadow-xl transition-shadow duration-300">
                <h4 className="font-bold text-brand-900 mb-4 text-lg">Response Time</h4>
                <div className="text-4xl font-bold text-primary-600 mb-2">2.3s</div>
                <p className="text-brand-600">Average response time</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-100 hover:shadow-xl transition-shadow duration-300">
                <h4 className="font-bold text-brand-900 mb-4 text-lg">Messages Handled</h4>
                <div className="text-4xl font-bold text-success-600 mb-2">1,247</div>
                <p className="text-brand-600">This month</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-100 hover:shadow-xl transition-shadow duration-300">
                <h4 className="font-bold text-brand-900 mb-4 text-lg">Customer Satisfaction</h4>
                <div className="text-4xl font-bold text-accent-600 mb-2">4.8/5</div>
                <p className="text-brand-600">Based on feedback</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-24 bg-brand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-900 mb-6">
              Instagram Marketing Insights
            </h2>
            <p className="text-xl text-brand-600 max-w-3xl mx-auto leading-relaxed">
              Stay ahead of the curve with expert tips on Instagram automation, engagement strategies, and AI-powered marketing techniques
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-brand-100 hover:border-brand-200 transform hover:-translate-y-1">
              <div className="w-full h-48 bg-brand-200 rounded-xl mb-6 relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=400&fit=crop&crop=center"
                  alt="Instagram automation strategies"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-2xl font-bold text-brand-900 mb-4 leading-tight">
                10 Instagram Conversation Automation Strategies That Will Boost Your Engagement by 300%
              </h3>
              <p className="text-brand-600 mb-6 leading-relaxed">
                Discover proven strategies to automate your Instagram conversations and skyrocket your engagement rates. Learn how AI-powered responses can transform your customer interactions.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm text-brand-500">
                  <span className="font-medium">Chatutu Team</span>
                  <span>•</span>
                  <span>8 min read</span>
                </div>
                <Link
                  to="/blog/10-instagram-conversation-automation-strategies"
                  className="text-primary-600 hover:text-primary-700 font-semibold transition-colors"
                >
                  Read More →
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-brand-100 hover:border-brand-200 transform hover:-translate-y-1">
              <div className="w-full h-48 bg-brand-200 rounded-xl mb-6 relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&crop=center"
                  alt="Business growth through automation"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-2xl font-bold text-brand-900 mb-4 leading-tight">
                Why Instagram Conversation Automation is Essential for Business Growth in 2024
              </h3>
              <p className="text-brand-600 mb-6 leading-relaxed">
                Explore how Instagram conversation automation tools are revolutionizing business communication. Learn about the latest AI technologies, automation best practices, and real-world case studies from successful Instagram marketers.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm text-brand-500">
                  <span className="font-medium">Coming Soon</span>
                  <span>•</span>
                  <span>12 min read</span>
                </div>
                <Link
                  to="/blog"
                  className="text-primary-600 hover:text-primary-700 font-semibold transition-colors"
                >
                  View All Posts →
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <Link
              to="/blog"
              className="inline-block px-10 py-4 bg-primary-600 text-white rounded-full font-semibold text-lg hover:bg-primary-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Read Our Blog
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of businesses already using Chatutu to streamline their Instagram communication
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Johnson',
                role: 'E-commerce Store Owner',
                content: 'Chatutu has transformed how we handle customer inquiries. Our response time dropped from hours to seconds, and our sales have increased by 40%.',
                rating: 5,
                avatar: 'SJ'
              },
              {
                name: 'Mike Chen',
                role: 'Digital Marketing Agency',
                content: 'The AI responses are incredibly natural. Our clients love that we can provide 24/7 support without actually being online 24/7.',
                rating: 5,
                avatar: 'MC'
              },
              {
                name: 'Emma Rodriguez',
                role: 'Fitness Coach',
                content: 'Managing multiple client conversations used to be overwhelming. Now Chatutu handles the routine questions while I focus on coaching.',
                rating: 5,
                avatar: 'ER'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-gray-600 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Automate Your Instagram Conversations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of businesses already saving time and boosting engagement with Chatutu
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowAuth('signup')}
              className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 shadow-lg"
            >
              Start Your Free Trial
            </button>
            <button
              onClick={() => setShowAuth('login')}
              className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all"
            >
              Sign In to Dashboard
            </button>
          </div>
          <p className="text-blue-100 mt-6 text-sm">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-lg">C</span>
                </div>
                <span className="text-xl font-bold">Chatutu</span>
              </div>
              <p className="text-gray-400">
                Automate your Instagram conversations with AI-powered responses and smart templates.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Features</button></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><button onClick={() => scrollToSection('reports')} className="hover:text-white transition-colors">Reports</button></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="hover:text-white transition-colors">Testimonials</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Chatutu. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* FAQ Schema (Hidden) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Instagram conversation automation?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Instagram conversation automation is the process of using AI-powered tools to automatically respond to conversations on Instagram. This helps businesses provide instant customer service, qualify leads, and maintain engagement 24/7 without manual intervention."
                }
              },
              {
                "@type": "Question",
                "name": "How does Chatutu's AI chatbot work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Chatutu uses advanced artificial intelligence and natural language processing to understand customer messages and provide relevant, personalized responses. The AI learns from your brand voice and past interactions to deliver human-like conversations."
                }
              },
              {
                "@type": "Question",
                "name": "Is Instagram conversation automation safe and compliant?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Chatutu is fully compliant with Instagram's terms of service and data privacy regulations. We use official Instagram APIs and follow all platform guidelines to ensure safe and ethical automation practices."
                }
              },
              {
                "@type": "Question",
                "name": "How much does Chatutu cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Chatutu offers a free trial to get started. Our pricing plans are designed for businesses of all sizes, starting from our basic plan for small businesses to enterprise solutions for large organizations."
                }
              },
              {
                "@type": "Question",
                "name": "Can I customize the AI responses?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely! Chatutu allows you to create custom response templates, set up automated workflows, and train the AI on your brand voice. You have full control over how the automation handles different types of customer inquiries."
                }
              }
            ]
          })
        }}
      />

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setShowAuth(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-8">
              {showAuth === 'login' ? <LoginPage /> : <SignupPage />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}